import React from 'react';
import { MAIN_MOCKUP_URL } from '../data/assets';
type ProductStackProps = {
  label?: string;
  className?: string;
};
type Cover = {
  n: string;
  title: string;
  sub: string;
  band: string;
  ink: string;
};
const covers: Cover[] = [{
  n: '02',
  title: 'The Array',
  sub: 'Modules, Strings, Wiring & Mounting',
  band: 'bg-vol-2',
  ink: 'text-pine'
}, {
  n: '04',
  title: 'Batteries, Hybrid & Off-Grid',
  sub: 'BMS, state of charge, MPPT, backup',
  band: 'bg-vol-4',
  ink: 'text-white'
}, {
  n: '01',
  title: 'The Diagnostic Toolkit',
  sub: '& Safe Service Visit',
  band: 'bg-vol-1',
  ink: 'text-white'
}];
const checkpoints = ['SYMPTOM', 'CODE', 'SPLIT', 'PROVE', 'FIX & FILE'];

/** Unifilar mínimo: arreglo → inversor → batería / red. DC en ámbar, AC en azul. */
function MiniDiagram() {
  return <svg viewBox="0 0 200 44" className="mt-[5%] w-full" aria-hidden="true" data-id="element-314" data-mp="w1n4x0d0uw6b">
      <g fill="none" strokeWidth="2" strokeLinejoin="round" data-id="element-315" data-mp="112ue93utc3f3">
        <rect x="4" y="8" width="38" height="26" rx="3" stroke="#14263B" data-id="element-316" data-mp="1j1j4wtvkg5da" />
        <path d="M17 8v26M29 8v26M4 21h38" stroke="#14263B" strokeWidth="1.2" data-id="element-317" data-mp="1e2fzv9fl5z5b" />
        <path d="M42 21h34" stroke="#F5A623" strokeWidth="2.5" data-id="element-318" data-mp="1e4izml6jj6n8" />
        <rect x="76" y="8" width="34" height="26" rx="3" stroke="#14263B" data-id="element-319" data-mp="1a1dzgp9xnh1r" />
        <path d="M84 25c3-8 6-8 9 0s6 8 9 0" stroke="#1F4E8C" strokeWidth="1.6" data-id="element-320" data-mp="1529ru32nprdj" />
        <path d="M110 21h30" stroke="#1F4E8C" strokeWidth="2.5" data-id="element-321" data-mp="21riqv7w414ch" />
        <path d="M93 34v6h60v-6" stroke="#F5A623" strokeWidth="1.6" data-id="element-322" data-mp="21tv4x9yqyuk9" />
        <rect x="140" y="12" width="26" height="18" rx="2" stroke="#14263B" data-id="element-323" data-mp="1xrgloor61ssg" />
        <path d="M166 21h12M182 10v22M188 14v14M194 18v6" stroke="#1F4E8C" strokeWidth="2" data-id="element-324" data-mp="1synhc1zm0mgw" />
      </g>
    </svg>;
}
function VolumeCover({
  cover,
  className
}: {
  cover: Cover;
  className: string;
}) {
  return <div className={`absolute aspect-[4/3] overflow-hidden rounded-lg bg-paper shadow-stack ring-1 ring-black/10 ${className}`} data-id="element-325" data-mp="2lgt48f8dvd7f">
      <div className={`${cover.band} px-[6%] pb-[5%] pt-[5%]`} data-id="element-326" data-mp="2gmygn0pnqe32">
        <span className={`block font-mono text-[8px] font-bold uppercase tracking-[1.5px] opacity-90 sm:text-[9px] ${cover.ink}`} data-id="element-327" data-mp="2zml0cniuk36r">
          Volume {cover.n}
        </span>
        <span className={`mt-0.5 block font-display text-[15px] leading-tight sm:text-xl ${cover.ink}`} data-id="element-328" data-mp="2qrx05bs4pbot">
          {cover.title}
        </span>
        <span className={`block text-[8px] opacity-85 sm:text-[10px] ${cover.ink}`} data-id="element-329" data-mp="2gv1zoncap17w">
          {cover.sub}
        </span>
      </div>
      <div className="px-[6%] py-[5%]" data-id="element-330" data-mp="2luwp0mgf9iqu">
        <div className="flex gap-[3px]" data-id="element-331" data-mp="2lrsfd9k6tuss">
          {checkpoints.map((c, i) => <span key={c} className={`flex-1 truncate rounded-[3px] px-0.5 py-[2px] text-center font-mono text-[5px] font-bold sm:text-[6px] ${i === 3 ? 'bg-ember text-pine' : 'bg-paper-dark text-pine-soft'}`} data-id="element-332" data-mp="27vx1xj8k5bws" data-mpi={i}>
              {c}
            </span>)}
        </div>
        <MiniDiagram data-id="element-333" data-mp="1um3jojcxg6b6" />
        <span className="mt-[4%] block text-[6px] font-bold uppercase tracking-[1.2px] text-pine-muted sm:text-[7px]" data-id="element-334" data-mp="1uneqyt5kzw71">
          Solar PV &amp; Battery Diagnostic Pro
        </span>
      </div>
    </div>;
}

/**
 * Imagen de producto. Si hay mockup principal (data/assets.ts) se usa ese;
 * si no, un abanico con tres tapas de tomo diseñadas con la identidad DPS65.
 */
export function ProductStack({
  label = '6 volumes · 159 cards',
  className = ''
}: ProductStackProps) {
  if (MAIN_MOCKUP_URL) {
    return <img src={MAIN_MOCKUP_URL} alt="The six Solar PV & Battery Diagnostic Pro volumes and five bonuses" className={`mx-auto h-auto w-full rounded-2xl shadow-stack ring-1 ring-black/10 ${className}`} data-id="element-335" data-mp="29ardol74k7z0" />;
  }
  return <div role="img" aria-label="Solar PV & Battery Diagnostic Pro volume covers: The Diagnostic Toolkit, The Array, and Batteries, Hybrid & Off-Grid" className={`relative mx-auto aspect-[20/17] w-full max-w-[460px] ${className}`} data-id="element-336" data-mp="swrexdk0modc">
      <VolumeCover cover={covers[0]} className="left-0 top-[18%] w-[58%] -rotate-[7deg]" data-id="element-337" data-mp="qcbnboxtx2u9" />
      <VolumeCover cover={covers[1]} className="right-0 top-[18%] w-[58%] rotate-[7deg]" data-id="element-338" data-mp="uclsx0hplr8l" />
      <VolumeCover cover={covers[2]} className="left-[18%] top-[3%] z-10 w-[64%]" data-id="element-339" data-mp="zekvi85mkk4h" />
      <span className="absolute bottom-0 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full bg-ember px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-pine shadow-card" data-id="element-340" data-mp="14bui7g1mqhom">
        {label}
      </span>
    </div>;
}